<template>
  <div class="enchanting-workbench stack tight">
    <UiPanel tone="work">
      <div class="header">
        <div style="min-width: 0;">
          <h3 class="title">{{ ICONS.skills.enchanting }} Enchanting</h3>
          <div class="subtitle text-muted">Apply or remove enchantments on unequipped item instances.</div>
        </div>
        <UiChip variant="muted" class="stat-mono num">
          Lv {{ formatNumber(enchantingLevel) }}
          <span v-if="levelDelta !== 0"> ({{ levelDelta > 0 ? `+${levelDelta}` : `${levelDelta}` }})</span>
        </UiChip>
      </div>
    </UiPanel>

    <UiCallout variant="info">
      Only unequipped item instances appear here. Unequip gear first if you want to enchant it.
    </UiCallout>

    <UiPanel tone="work">
      <UiSectionHeader>
        <template #title>Choose Item</template>
      </UiSectionHeader>
      <UiCallout v-if="instanceOptions.length === 0" variant="info">No enchantable items.</UiCallout>
      <div v-else class="select-grid">
        <button
          v-for="opt in instanceOptions"
          :key="opt.instanceId"
          type="button"
          class="select-card"
          :class="{ active: opt.instanceId === selectedInstanceId, disabled: opt.isLegacy }"
          :disabled="disabled"
          @click="selectedInstanceId = opt.instanceId"
        >
          <div class="select-card__name">
            {{ opt.label }}
            <UiChip v-if="opt.isLegacy" variant="warning">Legacy</UiChip>
          </div>
          <div class="text-muted">
            <template v-if="opt.enchantment">Enchanted · {{ opt.enchantment.name }}</template>
            <template v-else>Unenchanted</template>
          </div>
        </button>
      </div>
      <div v-if="selectedInstance?.isLegacy" class="text-muted" style="margin-top: 0.35rem;">Legacy items cannot be enchanted.</div>
      <div v-else-if="selectedInstance?.enchantment" class="text-muted" style="margin-top: 0.35rem;">
        Currently enchanted: {{ describeEffect(selectedInstance.enchantment) }}
        · Disenchant Mana {{ formatNumber(disenchantManaCost) }}
      </div>
      <div v-else class="text-muted" style="margin-top: 0.35rem;">No enchantment applied.</div>
    </UiPanel>

    <div class="action-row">
      <UiButton size="sm" tone="work" :disabled="!canApply" :title="applyDisabledReason || undefined" @click="emitApply">
        <template #icon>{{ ICONS.states.active }}</template>
        Apply
      </UiButton>
      <UiButton size="sm" variant="secondary" :disabled="!canRemove" :title="removeDisabledReason || undefined" @click="emitRemove">
        <template #icon>{{ ICONS.states.danger }}</template>
        Disenchant
      </UiButton>
    </div>
    <div v-if="applyDisabledReason || removeDisabledReason" class="action-hint text-muted">
      <div v-if="applyDisabledReason">{{ applyDisabledReason }}</div>
      <div v-if="removeDisabledReason">{{ removeDisabledReason }}</div>
    </div>

    <UiPanel tone="work">
      <UiSectionHeader>
        <template #title>Choose Enchantment</template>
      </UiSectionHeader>
      <UiCallout v-if="sortedEnchantments.length === 0" variant="info">No enchantments available.</UiCallout>
      <div v-else class="select-grid">
        <button
          v-for="ench in sortedEnchantments"
          :key="ench.id"
          type="button"
          class="select-card"
          :class="{ active: ench.id === selectedEnchantmentId, disabled: isLocked(ench) }"
          :disabled="disabled || isLocked(ench)"
          @click="selectedEnchantmentId = ench.id"
        >
          <div class="select-card__name">
            {{ ench.name }}
            <UiChip v-if="isLocked(ench)" variant="warning">Locked</UiChip>
          </div>
          <div class="text-muted">Lv {{ ench.levelReq || 1 }} · {{ describeEffect(ench) }}</div>
          <div class="text-muted stat-mono num">XP {{ formatNumber(ench.xp || 0) }}</div>
          <div class="text-muted stat-mono num">Mana {{ formatNumber(manaCostForEnchantment(ench)) }}</div>
        </button>
      </div>
      <div v-if="selectedEnchantment" class="text-muted" style="margin-top: 0.35rem;">
        {{ describeEffect(selectedEnchantment) }} • {{ formatNumber(selectedEnchantment.xp || 0) }} XP
        · Mana {{ formatNumber(selectedEnchantmentManaCost) }}
        <UiChip v-if="isLocked(selectedEnchantment)" variant="warning" style="margin-left: 0.5rem;">Locked</UiChip>
      </div>
    </UiPanel>

  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { Enchantment, InventoryEntry, SkillId, SkillProgress } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatNumber } from "../../../lib/format";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

type InstanceOption = {
  instanceId: string;
  label: string;
  enchantment?: Enchantment | null;
  isLegacy?: boolean;
};

const props = defineProps<{
  enchantments: Enchantment[];
  inventory: InventoryEntry[];
  skillProgress: SkillProgress[];
  skillModifiers?: Partial<Record<SkillId, number>> | null;
  currentMana: number;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  apply: [payload: { instanceId: string; enchantmentId: string }];
  remove: [payload: { instanceId: string }];
}>();

const selectedInstanceId = ref<string>("");
const selectedEnchantmentId = ref<string>("");

const enchantingLevel = computed(() => {
  const base = props.skillProgress.find((s) => s.id === "enchanting")?.level ?? 1;
  return Math.max(1, base);
});

function getModifier(skillId: SkillId): number {
  const raw = (props.skillModifiers || {})[skillId];
  const mod = Math.floor(Number(raw || 0));
  return Number.isFinite(mod) ? mod : 0;
}

const effectiveEnchantingLevel = computed(() => {
  const mod = getModifier("enchanting" as SkillId);
  return Math.max(1, Math.min(99, enchantingLevel.value + mod));
});

const levelDelta = computed(() => effectiveEnchantingLevel.value - enchantingLevel.value);

const instanceOptions = computed<InstanceOption[]>(() => {
  const opts: InstanceOption[] = [];
  for (const entry of props.inventory) {
    if (!entry.instanceIds || !entry.item?.equipmentSlot) continue;
    for (let i = 0; i < entry.instanceIds.length; i++) {
      const id = entry.instanceIds[i];
      const suffix = entry.instanceIds.length > 1 ? ` #${i + 1}` : "";
      opts.push({
        instanceId: id,
        label: `${entry.item.name}${suffix}`,
        enchantment: entry.enchantment,
        isLegacy: entry.isLegacy
      });
    }
  }
  return opts;
});

const selectedInstance = computed(() => instanceOptions.value.find((opt) => opt.instanceId === selectedInstanceId.value) ?? null);

const sortedEnchantments = computed(() => {
  return [...props.enchantments].sort((a, b) => (a.levelReq || 1) - (b.levelReq || 1) || a.name.localeCompare(b.name));
});

watch(
  () => sortedEnchantments.value,
  (next) => {
    if (next.length > 0 && !selectedEnchantmentId.value) selectedEnchantmentId.value = next[0].id;
  },
  { immediate: true }
);

watch(
  () => instanceOptions.value,
  (next) => {
    if (next.length > 0 && !selectedInstanceId.value) selectedInstanceId.value = next[0].instanceId;
  },
  { immediate: true }
);

const selectedEnchantment = computed(() => sortedEnchantments.value.find((e) => e.id === selectedEnchantmentId.value) ?? null);

function manaCostForEnchantment(enchantment: Enchantment): number {
  return Math.ceil(Number(enchantment.xp || 0) / 5);
}

function manaCostForDisenchant(enchantment: Enchantment): number {
  return Math.ceil(Number(enchantment.xp || 0) / 10);
}

function describeEffect(enchantment: Enchantment): string {
  if (enchantment.effectType === "attribute") {
    const target = (enchantment.target || "attr").toString().toUpperCase();
    return `+${enchantment.amount} ${target}`;
  }
  if (enchantment.effectType === "skill") {
    const target = (enchantment.target || "skill").toString();
    return `+${enchantment.amount} ${target.charAt(0).toUpperCase() + target.slice(1)}`;
  }
  if (enchantment.effectType === "ward") {
    const pool = (enchantment.wardType || "hp").toUpperCase();
    return `${enchantment.amount} ${pool} Ward (regenerating)`;
  }
  return enchantment.description || "Unknown effect";
}

function isLocked(enchantment: Enchantment): boolean {
  const req = enchantment.levelReq || 1;
  return effectiveEnchantingLevel.value < req;
}

const canApply = computed(() => {
  if (props.disabled) return false;
  if (!selectedInstance.value || !selectedEnchantment.value) return false;
  if (selectedInstance.value.isLegacy) return false;
  if (selectedInstance.value.enchantment) return false;
  if (!hasEnoughManaForApply.value) return false;
  return !isLocked(selectedEnchantment.value);
});

const canRemove = computed(() => {
  if (props.disabled) return false;
  if (!selectedInstance.value) return false;
  if (selectedInstance.value.isLegacy) return false;
  if (!hasEnoughManaForDisenchant.value) return false;
  return !!selectedInstance.value.enchantment;
});

const selectedEnchantmentManaCost = computed(() =>
  selectedEnchantment.value ? manaCostForEnchantment(selectedEnchantment.value) : 0
);

const disenchantManaCost = computed(() =>
  selectedInstance.value?.enchantment ? manaCostForDisenchant(selectedInstance.value.enchantment) : 0
);

const hasEnoughManaForApply = computed(() => props.currentMana >= selectedEnchantmentManaCost.value);
const hasEnoughManaForDisenchant = computed(() => props.currentMana >= disenchantManaCost.value);

const applyDisabledReason = computed(() => {
  if (selectedInstance.value?.enchantment) return "Already enchanted. Disenchant first.";
  if (!selectedEnchantment.value) return null;
  if (hasEnoughManaForApply.value) return null;
  return `Not enough mana (need ${formatNumber(selectedEnchantmentManaCost.value)}).`;
});

const removeDisabledReason = computed(() => {
  if (!selectedInstance.value?.enchantment) return null;
  if (hasEnoughManaForDisenchant.value) return null;
  return `Not enough mana (need ${formatNumber(disenchantManaCost.value)}).`;
});

function emitApply() {
  if (!canApply.value || !selectedInstance.value || !selectedEnchantment.value) return;
  emit("apply", { instanceId: selectedInstance.value.instanceId, enchantmentId: selectedEnchantment.value.id });
}

function emitRemove() {
  if (!canRemove.value || !selectedInstance.value) return;
  emit("remove", { instanceId: selectedInstance.value.instanceId });
}
</script>

<style scoped>
.enchanting-workbench {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  align-items: flex-start;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.select {
  width: 100%;
}

.select-grid {
  margin-top: var(--space-sm);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.65rem;
}

.select-card {
  border: 1px solid var(--border-0);
  border-radius: var(--radius-2);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  padding: 0.7rem 0.8rem;
  text-align: left;
  display: grid;
  gap: 0.35rem;
  cursor: pointer;
}

.select-card.active {
  border-color: color-mix(in srgb, var(--accent-work) 55%, var(--border-0));
  box-shadow: var(--shadow-accent);
}

.select-card.disabled {
  opacity: 0.7;
}

.select-card__name {
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.45rem 0.2rem;
}

.action-hint {
  font-size: 0.85rem;
  display: grid;
  gap: 0.2rem;
  padding: 0 0.2rem;
}

.enchant-grid {
  margin-top: var(--space-sm);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
}

.enchant-card {
  border: 1px solid var(--border-0);
  border-radius: var(--radius-2);
  background: color-mix(in srgb, var(--bg-2) 78%, transparent);
  padding: 0.75rem;
  display: grid;
  gap: 0.3rem;
}

.enchant-card.locked {
  opacity: 0.78;
}

.enchant-name {
  font-weight: 850;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
